import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Navigate, useNavigate } from 'react-router-dom';

// Types
import { AuthFormData } from '../../types/auth-form-data';

// Store
import { RootState } from '../../store/store';
import { Dispatch } from '../../store/store';

// Constants
import ROUTE from '../../constants/route-constants';

// Components
import IconMobileTailAdmin from '../../components/Icons/MobileTailAdmin/MobileTailAdmin';
import { useTranslation } from 'react-i18next';

type ForgotPasswordProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ isAuthenticated, forgotPassword }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AuthFormData>({
    email: null,
    password_digest: null,
  });

  // Logic
  if (isAuthenticated) {
    return <Navigate to={ROUTE.HOME} />;
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.email) {
      try {
        await forgotPassword(formData.email);
        navigate(ROUTE.AUTH_CONFIRM_PASSWORD_RESET, { state: { email: formData.email } });
      } catch (error) {
        console.error('Failed to initiate password reset:', error);
      }
    }
  };

  return (
    <div className="p-sign-in h-screen flex md:items-center md:justify-center md:p-6">
      <div className="p-sign-in__wrapper w-full md:w-auto grid rounded-sm items-center border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark md:grid-cols-[minmax(auto,_564px)_minmax(350px,_564px)] md:grid-rows-[600px]">
        <div className="p-sign-in__left-side flex justify-center items-center h-full w-full bg-primary">
          <IconMobileTailAdmin width="100%" height="100%" iconClassName='max-w-[389px] max-h-[350px] md:w-[389px] md:h-[350px]' />
        </div>
        <div className="p-sign-in__right-side flex flex-col w-full h-full py-13 px-11 border-stroke dark:border-strokedark xl:border-l-2">
          <div className='p-sign-in__text-form pb-9'>
            <span className="p-sign-in__greetings pb-1.5 block font-medium">
              {t('labels.cycle.auth.resetPassword')}
            </span>
            <h2 className="p-sign-in__title text-2xl font-bold text-black dark:text-white sm:text-title-xl2">
              {t('labels.cycle.auth.enterEmail')}
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {t('labels.cycle.auth.verificationCode')}
            </p>
          </div>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="mb-4">
              <label htmlFor="email" className="mb-2.5 block font-medium text-black dark:text-white">
                {t('labels.cycle.auth.email')}
              </label>
              <div className="relative">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email || ''}
                  onChange={handleChange}
                  placeholder={t('labels.cycle.auth.enterEmail')}
                  className="w-full rounded-lg border border-stroke bg-transparent py-3 pl-6 pr-10 outline-none focus:border-primary focus-visible:shadow-none dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary"
                  required
                />
              </div>
            </div>
            <button
              type="submit"
              className="flex w-full justify-center rounded bg-primary p-3 font-medium text-white hover:bg-opacity-90"
            >
              {t('labels.cycle.auth.sendResetCode')}
            </button>
            <div className="mt-4 text-center">
              <button
                onClick={() => navigate(ROUTE.AUTH_SIGN_IN)}
                className="text-sm text-primary hover:text-primary-dark transition-colors"
              >
                {t('labels.cycle.auth.backToSignIn')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const mapState = (state: RootState) => ({
  isAuthenticated: state.auth?.isAuthenticated,
});

const mapDispatch = (dispatch: Dispatch) => ({
  forgotPassword: (email: string) => dispatch.auth.forgotPassword(email),
});

export default connect(mapState, mapDispatch)(ForgotPassword);
